import { useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchPublishedStories, fetchPublishedStory, StaleTokenError } from '../api/stories';
import { isMarchandViveretHost, readBearerToken, subscribeToBearerToken } from '../grampsAuth';

function useToken(treeName: string | null | undefined) {
    return useSyncExternalStore(subscribeToBearerToken, () => readBearerToken(treeName)) ?? undefined;
}

// Not the token in the key: it is renewed every few minutes, and a new key
// would load the story again, unmounting its player mid-listening. A stale
// token's 401 is retried instead, once the refresh it set off has had time
// to land; each try reads the token afresh.
const STALE_TOKEN_RETRIES = 2;
const STALE_TOKEN_RETRY_MS = 2000;

const retryStaleToken = (failures: number, error: Error) =>
    error instanceof StaleTokenError && failures < STALE_TOKEN_RETRIES;

/** The published stories of the tree; with `person`, those about that person. */
export function usePublishedStories(treeName: string | null | undefined, person?: string | null) {
    const token = useToken(treeName);
    return useQuery({
        queryKey: ['stories', 'published', treeName, person ?? null],
        queryFn: () => fetchPublishedStories(readBearerToken(treeName) ?? undefined, person ?? undefined),
        enabled: Boolean(isMarchandViveretHost() || token),
        // Stories are published by hand, a few times a week at most.
        staleTime: 10 * 60 * 1000,
        retry: retryStaleToken,
        retryDelay: STALE_TOKEN_RETRY_MS,
    });
}

export function usePublishedStory(treeName: string | null | undefined, id: string | undefined) {
    const token = useToken(treeName);
    return useQuery({
        queryKey: ['stories', 'published', treeName, 'one', id],
        queryFn: () => fetchPublishedStory(id as string, readBearerToken(treeName) ?? undefined),
        enabled: Boolean(id && (isMarchandViveretHost() || token)),
        staleTime: 10 * 60 * 1000,
        retry: retryStaleToken,
        retryDelay: STALE_TOKEN_RETRY_MS,
    });
}
